import {
  LESSON_VIEWER_FETCH_START,
  LESSON_VIEWER_FETCH_SUCCESS,
  LESSON_VIEWER_FETCH_ERROR,
  LESSON_VIEWER_RESET,
  LESSON_VIEWER_SET_PHASE,
  LESSON_VIEWER_ADVANCE_TERM,
  LESSON_VIEWER_WORKBOOK_READY,
  LESSON_VIEWER_WORKBOOK_DONE,
  LESSON_VIEWER_WORKBOOK_SUBMIT,
  LESSON_VIEWER_KC_SUBMIT,
  LESSON_VIEWER_KC_NEXT,
  LESSON_VIEWER_KC_SUMMARY_DONE,
  LESSON_VIEWER_MASTERY_SUBMIT,
  LESSON_VIEWER_MASTERY_NEXT,
  LESSON_VIEWER_TOGGLE_DEV_TOOLS,
  LESSON_VIEWER_SET_TUTORIAL_VIEWED,
  LESSON_VIEWER_SET_TUTOR_CHAT_OPEN,
} from './actionTypes';
import {fetchLessonContent as fetchFromApi} from 'services/LessonContentService';
import {mapLessonContent} from 'services/LessonContentMapper';

const STORAGE_PREFIX = 'quiklearn_lcv_';

function getTutorialViewed(lessonId) {
  try {
    return localStorage.getItem(`${STORAGE_PREFIX}${lessonId}_tutorial`) === 'true';
  } catch {
    return false;
  }
}

function setTutorialViewedStorage(lessonId) {
  try {
    localStorage.setItem(`${STORAGE_PREFIX}${lessonId}_tutorial`, 'true');
  } catch {
    // silent
  }
}

function saveProgress(lessonId, progress) {
  try {
    localStorage.setItem(
      `${STORAGE_PREFIX}${lessonId}_progress`,
      JSON.stringify(progress),
    );
  } catch {
    // silent
  }
}

function saveTermCompletion(lessonId, termName) {
  try {
    const key = `${STORAGE_PREFIX}${lessonId}_termCompletion`;
    const existing = JSON.parse(localStorage.getItem(key) || '[]');
    if (!existing.includes(termName)) {
      existing.push(termName);
      localStorage.setItem(key, JSON.stringify(existing));
    }
  } catch {
    // silent
  }
}

export const fetchLessonContentAction = lessonId => async dispatch => {
  dispatch({type: LESSON_VIEWER_FETCH_START});

  try {
    const rawPayload = await fetchFromApi(lessonId);
    const viewerModel = mapLessonContent(rawPayload);
    const tutorialViewed = getTutorialViewed(lessonId);

    dispatch({
      type: LESSON_VIEWER_FETCH_SUCCESS,
      payload: {rawPayload, viewerModel, tutorialViewed},
    });
  } catch (error) {
    dispatch({
      type: LESSON_VIEWER_FETCH_ERROR,
      payload: error.message || 'Failed to load lesson content',
    });
  }
};

export const resetViewer = () => ({type: LESSON_VIEWER_RESET});

export const setPhase = phase => ({
  type: LESSON_VIEWER_SET_PHASE,
  payload: phase,
});

export const advanceTerm = termIndex => ({
  type: LESSON_VIEWER_ADVANCE_TERM,
  payload: termIndex,
});

export const setTutorialViewed = () => (dispatch, getState) => {
  const lessonId =
    getState().lessonContentViewer.viewerModel?.metadata?.lessonId;
  if (lessonId) {
    setTutorialViewedStorage(lessonId);
  }
  dispatch({type: LESSON_VIEWER_SET_TUTORIAL_VIEWED});
};

export const setWorkbookReady = () => ({type: LESSON_VIEWER_WORKBOOK_READY});
export const setWorkbookDone = () => ({type: LESSON_VIEWER_WORKBOOK_DONE});

export const submitWorkbookAnswer = optionIndex => (dispatch, getState) => {
  const state = getState().lessonContentViewer;
  const term = state.viewerModel?.terms?.[state.activeTermIndex];
  if (!term) return;

  const selectedAnswer = term.workbook.options[optionIndex];
  const correct = selectedAnswer === term.workbook.correctAnswer;

  dispatch({
    type: LESSON_VIEWER_WORKBOOK_SUBMIT,
    payload: {optionIndex, selectedAnswer, correct},
  });
};

export const advancePhase = () => (dispatch, getState) => {
  const state = getState().lessonContentViewer;
  const lessonId = state.viewerModel?.metadata?.lessonId;

  const phaseMap = {
    tutorial: 'frontloading',
    frontloading: 'learning',
    learning: 'workbook_ready',
    workbook_ready: 'workbook_exercise',
    workbook_exercise: 'workbook_options',
    workbook_options: 'knowledge_check',
  };

  const nextPhase = phaseMap[state.phase];
  if (nextPhase) {
    dispatch(setPhase(nextPhase));
    if (lessonId) {
      saveProgress(lessonId, {
        phase: nextPhase,
        activeTermIndex: state.activeTermIndex,
        activeQuestionIndex: 0,
      });
    }
  }
};

export const submitKCAnswer = selectedAnswer => (dispatch, getState) => {
  const state = getState().lessonContentViewer;
  const term = state.viewerModel?.terms?.[state.activeTermIndex];
  if (!term) return;

  const question = term.knowledgeCheck.questions[state.activeQuestionIndex];
  if (!question) return;

  const correct = selectedAnswer === question.correctAnswer;

  dispatch({
    type: LESSON_VIEWER_KC_SUBMIT,
    payload: {
      questionIndex: state.activeQuestionIndex,
      selectedAnswer,
      correct,
    },
  });
};

export const advanceKCQuestion = () => (dispatch, getState) => {
  const state = getState().lessonContentViewer;
  const term = state.viewerModel?.terms?.[state.activeTermIndex];
  if (!term) return;

  const totalQuestions = term.knowledgeCheck.questions.length;
  const nextIndex = state.activeQuestionIndex + 1;

  if (nextIndex < totalQuestions) {
    dispatch({type: LESSON_VIEWER_KC_NEXT});
  } else {
    dispatch(setPhase('knowledge_check_summary'));
  }
};

export const kcSummaryDone = () => (dispatch, getState) => {
  const state = getState().lessonContentViewer;
  const lessonId = state.viewerModel?.metadata?.lessonId;
  const term = state.viewerModel?.terms?.[state.activeTermIndex];

  if (term && lessonId) {
    saveTermCompletion(lessonId, term.term);
  }

  dispatch({type: LESSON_VIEWER_KC_SUMMARY_DONE});
};

export const submitMasteryAnswer = selectedAnswer => (dispatch, getState) => {
  const state = getState().lessonContentViewer;
  const questions = state.viewerModel?.mastery?.questions || [];
  const question = questions[state.activeQuestionIndex];
  if (!question) return;

  const correct = selectedAnswer === question.correctAnswer;

  dispatch({
    type: LESSON_VIEWER_MASTERY_SUBMIT,
    payload: {
      questionIndex: state.activeQuestionIndex,
      selectedAnswer,
      correct,
    },
  });
};

export const advanceMasteryQuestion = () => ({
  type: LESSON_VIEWER_MASTERY_NEXT,
});

export const toggleDevTools = () => ({type: LESSON_VIEWER_TOGGLE_DEV_TOOLS});

export const setTutorChatOpen = open => ({
  type: LESSON_VIEWER_SET_TUTOR_CHAT_OPEN,
  payload: open,
});
